/**
 * ============================================================================
 * useLanguage Hook - Gerenciamento de Idioma
 * ============================================================================
 *
 * Hook customizado para gerenciar o idioma da aplicação:
 * - Obter idioma atual
 * - Trocar idioma
 * - Sincronizar com localStorage
 * - Sincronizar com backend (usuários logados)
 */

import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '@/i18n/config';
import { LANGUAGE_STORAGE_KEY, DEFAULT_LANGUAGE } from '@/i18n/config';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

// Função helper para normalizar o idioma
function normalizeLanguage(lng: string): SupportedLanguage {
  // Se for um idioma suportado diretamente, retorna
  if (lng === 'en' || lng === 'pt-BR' || lng === 'pt-PT' || lng === 'es') {
    return lng;
  }
  // Fallback para en se for inglês genérico
  if (lng.startsWith('en')) {
    return 'en';
  }
  // Fallback para pt-BR se for português genérico
  if (lng.startsWith('pt')) {
    return 'pt-BR';
  }
  // Fallback para es se for espanhol genérico
  if (lng.startsWith('es')) {
    return 'es';
  }
  // Fallback padrão
  return DEFAULT_LANGUAGE;
}

export function useLanguage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  // Estado local para forçar re-renderização
  const [language, setLanguage] = useState<SupportedLanguage>(() => {
    const currentLang = i18n.language || localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE;
    return normalizeLanguage(currentLang);
  });

  // Sincronizar estado local com i18n
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLanguage(normalizeLanguage(lng));
    };

    i18n.on('languageChanged', handleLanguageChanged);

    // Sincronizar estado inicial
    if (i18n.language) {
      setLanguage(normalizeLanguage(i18n.language));
    }

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  /**
   * Troca o idioma da aplicação
   * - Atualiza i18next
   * - Salva em localStorage
   * - Sincroniza com backend (se usuário estiver logado)
   */
  const changeLanguage = useCallback(
    async (newLanguage: SupportedLanguage) => {
      try {
        // Atualizar estado local imediatamente
        setLanguage(newLanguage);

        // Atualizar i18next
        await i18n.changeLanguage(newLanguage);

        // Garantir que o localStorage está atualizado
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);

        // Sincronizar com backend se usuário estiver logado
        // Nota: Fazemos isso de forma assíncrona sem bloquear a UI
        if (user?.id) {
          api.patch('/usuarios/me/language', {
            preferred_language: newLanguage
          }).catch((error) => {
            // Silenciosamente ignora erros - a preferência já foi salva localmente
            console.warn('Failed to sync language preference to backend:', error?.message);
          });
        }
      } catch (error) {
        console.error('Error changing language:', error);
        // Reverter estado local em caso de erro
        setLanguage((i18n.language as SupportedLanguage) || DEFAULT_LANGUAGE);
        throw error;
      }
    },
    [i18n, user]
  );

  return {
    currentLanguage: language,
    changeLanguage,
  };
}
